import { useMemo, type KeyboardEvent, type ReactNode } from 'react';
import { clsx } from '../clsx';
import { Checkbox } from './Checkbox';
import { TextLink } from './TextLink';
import styles from './Table.module.css';

export interface TableColumn<Row> {
  id: string;
  header: ReactNode;
  /** Grid track of the column, e.g. "minmax(0,1.4fr)", "140px"; default "minmax(0,1fr)". */
  track?: string | undefined;
  align?: 'start' | 'center' | 'end' | undefined;
  /** Tabular figures, right-aligned unless `align` says otherwise. */
  numeric?: boolean | undefined;
  cell: (row: Row, index: number) => ReactNode;
}

export interface TableProps<Row> {
  columns: ReadonlyArray<TableColumn<Row>>;
  rows: ReadonlyArray<Row>;
  rowKey: (row: Row) => string;
  /** Accessible name of the table ("Kontakte"). */
  label: string;
  onRowClick?: ((row: Row) => void) | undefined;
  /** Adds the 28 px checkbox column and the dark bulk bar while something is selected. */
  selectable?: boolean | undefined;
  selected?: ReadonlySet<string> | undefined;
  onSelectionChange?: ((next: Set<string>) => void) | undefined;
  /** "3 Kontakte gewählt". */
  selectionLabel?: ((count: number) => string) | undefined;
  /** Buttons of the bulk bar (`Button variant="ghostInverse" size="small"`). */
  bulkActions?: ReactNode | undefined;
  /** `sunken`: eyebrow header on `--surface-sunken` (settings tables); `plain`: eyebrow header inside a flush card (Kontakte 01). */
  headerTone?: 'sunken' | 'plain' | undefined;
  /** `default` 13/20 rows (lists); `compact` 10/12 rows (settings). */
  density?: 'default' | 'compact' | undefined;
  /** Shown instead of rows when `rows` is empty. */
  emptyText?: ReactNode | undefined;
  rowClassName?: ((row: Row) => string | undefined) | undefined;
  className?: string | undefined;
}

/** Grid-row table of the screens: eyebrow header, hairline rows, `--sand-50` hover, selection with dark bulk bar. */
export function Table<Row>({
  columns,
  rows,
  rowKey,
  label,
  onRowClick,
  selectable = false,
  selected,
  onSelectionChange,
  selectionLabel = (count) => `${count} gewählt`,
  bulkActions,
  headerTone = 'sunken',
  density = 'default',
  emptyText,
  rowClassName,
  className,
}: TableProps<Row>) {
  const tracks = useMemo(
    () =>
      [selectable ? '28px' : null, ...columns.map((column) => column.track ?? 'minmax(0,1fr)')]
        .filter(Boolean)
        .join(' '),
    [columns, selectable],
  );
  const selection = selected ?? new Set<string>();
  const allKeys = useMemo(() => rows.map(rowKey), [rows, rowKey]);
  const allSelected = allKeys.length > 0 && allKeys.every((key) => selection.has(key));
  const someSelected = allKeys.some((key) => selection.has(key));

  function toggle(key: string) {
    const next = new Set(selection);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange?.(next);
  }

  function toggleAll() {
    onSelectionChange?.(allSelected ? new Set() : new Set(allKeys));
  }

  function onRowKeyDown(event: KeyboardEvent<HTMLDivElement>, row: Row) {
    if (!onRowClick || event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick(row);
    }
  }

  const hasBar = selectable && selection.size > 0;

  return (
    <div
      role="table"
      aria-label={label}
      className={clsx(
        styles.table,
        headerTone === 'plain' ? styles.plain : styles.sunken,
        density === 'compact' && styles.compact,
        hasBar && styles.hasBar,
        className,
      )}
    >
      {hasBar ? (
        <div className={styles.bulkBar}>
          <span className={styles.bulkLabel}>{selectionLabel(selection.size)}</span>
          <span className={styles.bulkActions}>
            {bulkActions}
            <TextLink as="button" className={styles.bulkClear} onClick={() => onSelectionChange?.(new Set())}>
              Auswahl aufheben
            </TextLink>
          </span>
        </div>
      ) : null}
      <div role="rowgroup">
        <div role="row" className={styles.header} style={{ gridTemplateColumns: tracks }}>
          {selectable ? (
            <span role="columnheader" className={styles.selectCell}>
              <Checkbox
                label="Alle auswählen"
                labelHidden
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={toggleAll}
              />
            </span>
          ) : null}
          {columns.map((column) => (
            <span
              key={column.id}
              role="columnheader"
              className={clsx(
                styles.headerCell,
                (column.align === 'end' || (column.numeric && !column.align)) && styles.alignEnd,
                column.align === 'center' && styles.alignCenter,
              )}
            >
              {column.header}
            </span>
          ))}
        </div>
      </div>
      <div role="rowgroup">
        {rows.length === 0 && emptyText ? (
          <div role="row" className={styles.empty}>
            <span role="cell">{emptyText}</span>
          </div>
        ) : null}
        {rows.map((row, index) => {
          const key = rowKey(row);
          const isSelected = selection.has(key);
          return (
            <div
              key={key}
              role="row"
              aria-selected={selectable ? isSelected : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              className={clsx(
                styles.row,
                onRowClick && styles.clickable,
                isSelected && styles.selected,
                rowClassName?.(row),
              )}
              style={{ gridTemplateColumns: tracks }}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={(event) => onRowKeyDown(event, row)}
            >
              {selectable ? (
                <span role="cell" className={styles.selectCell} onClick={(event) => event.stopPropagation()}>
                  <Checkbox label="Auswählen" labelHidden checked={isSelected} onChange={() => toggle(key)} />
                </span>
              ) : null}
              {columns.map((column) => (
                <span
                  key={column.id}
                  role="cell"
                  className={clsx(
                    styles.cell,
                    column.numeric && styles.numeric,
                    (column.align === 'end' || (column.numeric && !column.align)) && styles.alignEnd,
                    column.align === 'center' && styles.alignCenter,
                  )}
                >
                  {column.cell(row, index)}
                </span>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
